"use client";

import { useEffect, useState } from "react";
import Link from "@/components/locale-link";

const CONSENT_KEY = "cookie-consent";

/**
 * The banner renders on every page including the 16 localized trees, so its copy is
 * overridable. Defaults are the English strings, so English callers pass nothing.
 */
export interface CookieConsentStrings {
  notice: string;
  privacyPolicy: string;
  accept: string;
  decline: string;
}

const DEFAULT_STRINGS: CookieConsentStrings = {
  notice:
    "We use cookies for analytics and advertising (Google Analytics & Adsterra) to keep CitationEasy free.",
  privacyPolicy: "Privacy Policy",
  accept: "Accept",
  decline: "Decline",
};

export function CookieConsent({
  strings = DEFAULT_STRINGS,
  privacyHref = "/privacy",
}: {
  strings?: CookieConsentStrings;
  privacyHref?: string;
} = {}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setVisible(true);
    }
  }, []);

  function accept() {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setVisible(false);
  }

  function decline() {
    localStorage.setItem(CONSENT_KEY, "declined");
    setVisible(false);
    // Disable Google Analytics
    window.gtag?.("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-border/60 border-t bg-background/95 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lg backdrop-blur-sm sm:px-6 sm:py-4">
      <div className="mx-auto flex max-w-5xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
        <p className="flex-1 text-muted-foreground text-sm leading-relaxed">
          {strings.notice}{" "}
          <Link
            className="underline underline-offset-4 hover:text-foreground"
            href={privacyHref}
          >
            {strings.privacyPolicy}
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            className="rounded-md border border-border px-4 py-2 font-medium text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground"
            onClick={decline}
            type="button"
          >
            {strings.decline}
          </button>
          <button
            className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90"
            onClick={accept}
            type="button"
          >
            {strings.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
